import React, { useMemo } from 'react';

import { Alert, Paper, Stack, Switch, Table, Text, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconAlertTriangle } from '@tabler/icons-react';
import useAutoCompleteAppointmentType from 'hooks/appointmentType/useAutoCompleteAppointmentType';
import useAutoCompleteClassType from 'hooks/classType/useAutoCompleteClassType';
import useAutoCompletePackage from 'hooks/package/useAutoCompletePackage';

import {
  ACTION_LABELS,
  FormValues,
  KiloGLMFlowActions,
  KiloGLMFlowTriggers,
  TRIGGER_LABELS,
} from '../types';

// Lifecycle + action triggers must be explicitly enabled to do anything.
// Data-sync (Athlete Data Updated) and note triggers default ON, so saving them
// disabled is intentional and silent.
const SILENT_WHEN_DISABLED = new Set<string>([
  KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED,
  KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED,
  KiloGLMFlowTriggers.NOTE_DELETED,
]);

interface IKiloGLMEnableSwitchProps {
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>;
}

const KiloGLMEnableSwitch = ({ form }: IKiloGLMEnableSwitchProps): JSX.Element => {
  const {
    trigger,
    action,
    packageIds,
    athleteStatus,
    checkinsNumber,
    appointmentTypeIds,
    classTypeIds,
    acPipelineId,
    acStageId,
    tags,
  } = form.values;

  const { data: packageData } = useAutoCompletePackage({});
  const { data: appData } = useAutoCompleteAppointmentType({});
  const { data: classData } = useAutoCompleteClassType({});

  const conditions = useMemo((): string => {
    if (trigger.includes('Package') && packageIds?.length) {
      const map = new Map((packageData?.data.results ?? []).map(p => [p.packageId, p.name]));
      return packageIds.map(id => map.get(id) ?? id).join(', ');
    }
    if (trigger === KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED && athleteStatus) {
      return athleteStatus;
    }
    if (trigger === KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED && (checkinsNumber ?? 0) > 0) {
      return `${checkinsNumber} check-ins`;
    }
    if (trigger === KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN && appointmentTypeIds?.length) {
      const map = new Map((appData?.data ?? []).map(a => [a.appointmentTypeId, a.name]));
      return appointmentTypeIds.map(id => map.get(id) ?? id).join(', ');
    }
    if (trigger === KiloGLMFlowTriggers.CLASS_CHECKIN && classTypeIds?.length) {
      const map = new Map((classData?.data.results ?? []).map(c => [c.kiloClassTypeId, c.name]));
      return classTypeIds.map(id => map.get(id) ?? id).join(', ');
    }
    return '';
  }, [
    trigger,
    packageIds,
    athleteStatus,
    checkinsNumber,
    appointmentTypeIds,
    classTypeIds,
    packageData,
    appData,
    classData,
  ]);

  const startsWhen = [TRIGGER_LABELS[trigger as KiloGLMFlowTriggers] ?? trigger, conditions]
    .filter(Boolean)
    .join(' — ');
  const thenText = ACTION_LABELS[action as KiloGLMFlowActions] ?? action ?? '-';
  const detailsText =
    (tags?.length ? tags.join(', ') : '') ||
    (acPipelineId && acStageId ? 'Pipeline stage selected' : '') ||
    '-';

  const showDisabledWarning = !!trigger && !SILENT_WHEN_DISABLED.has(trigger) && !form.values.enabled;

  return (
    <Paper>
      <Stack justify='center' align='center' sx={{ paddingTop: '20px' }}>
        <>
          <Title order={3} align='left' style={{ alignSelf: 'flex-start' }}>
            Looks good?
          </Title>
          <Text size={15} style={{ alignSelf: 'flex-start' }}>
            Review your automation before saving.
          </Text>
          <Table withBorder withColumnBorders style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 600, whiteSpace: 'nowrap', width: '30%' }}>Starts when</td>
                <td>{startsWhen || '-'}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Then</td>
                <td>{thenText}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 600 }}>Details</td>
                <td>{detailsText}</td>
              </tr>
            </tbody>
          </Table>
          <Switch
            label='Activate this automation now'
            description='You can always turn it on or off from your automations list.'
            size='md'
            checked={form.values.enabled}
            onChange={event => form.setFieldValue('enabled', event.currentTarget.checked)}
            style={{ alignSelf: 'flex-start' }}
          />
          {showDisabledWarning && (
            <Alert
              icon={<IconAlertTriangle size={16} />}
              color='yellow'
              variant='light'
              style={{ alignSelf: 'flex-start', width: '100%' }}
            >
              This automation is turned off and won&apos;t run until you activate it.
            </Alert>
          )}
        </>
      </Stack>
    </Paper>
  );
};

export default KiloGLMEnableSwitch;
